import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip, useNotify } from '@routes-plan/ui';
import { Route } from '../../../models/route';
import { Office } from '../../../models/office';
import PermissionButton from '../../../components/common/PermissionButton';
import { useSelection } from '../../../components/context/SelectionContext';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, ContentCard, FilterRow, RowAction, RowActions, apiError, useConfirm } from '../shared/AdminUi';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface RouteRow {
    id: number;
    officeName: string;
    routeName: string;
    time: string;
    startDate: string;
    responsible: string;
    assigned: boolean;
}

const RouteList: React.FC<ShowAlerts> = () => {
    const t = useT();
    const notify = useNotify();
    const [confirm, confirmDialog] = useConfirm();
    setPageTitle('routes.pageTitle');
    const [routes, setRoutes] = useState<Route[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [loading, setLoading] = useState(false);
    const { office_id, setOffice_id } = useSelection();
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/routes');

    // Cargar sedes
    useEffect(() => {
        (async () => {
            try {
                const officesResponse = await axios.get('offices/all');
                setOffices(Array.isArray(officesResponse.data?.data) ? officesResponse.data.data : []);
            } catch (error) {
                notify.error(t('routes.officesLoadError'));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!office_id) {
            setRoutes([]);
            return;
        }
        (async () => {
            setLoading(true);
            try {
                setRoutes([]);
                const { data } = await axios.get(`offices/${office_id}/routes`);
                setRoutes(Array.isArray(data) ? data : []);
            } catch (error) {
                notify.error(t('common.loadError'));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [office_id]);

    const handleDeleteRoute = async (row: RouteRow) => {
        if (!canDelete) return;
        const ok = await confirm({ title: t('routes.confirmDeleteTitle', { name: row.routeName }), message: t('routes.confirmDelete') });
        if (!ok) return;
        try {
            await axios.delete(`/routes/${row.id}`);
            setRoutes((prev) => prev.filter((route: Route) => route.id !== row.id));
            notify.success(t('routes.deletedOk'));
        } catch (error) {
            notify.error(apiError(error, t('routes.deleteError')));
        }
    };

    const rows: RouteRow[] = routes.map((route: Route) => {
        let ingressDate = '—';
        if (route.startDate) {
            try {
                ingressDate = format(parseISO(route.startDate), 'yyyy-MM-dd');
            } catch (error) {
                ingressDate = route.startDate;
            }
        }
        return {
            id: route.id,
            officeName: route.office?.name ?? t('routes.noOffice'),
            routeName: route.name,
            time: route.openingTime && route.closingTime ? `${route.openingTime} – ${route.closingTime}` : '—',
            startDate: ingressDate,
            responsible: route.user ? `${route.user.firstName} ${route.user.lastName}` : t('routes.unassigned'),
            assigned: Boolean(route.user),
        };
    });

    const addButton = (
        <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            variant="contained"
            startIcon={<AddIcon />}
            component={NavLink}
            to="/admin/routes/new"
        >
            {t('routes.newTitle')}
        </PermissionButton>
    );

    return (
        <AdminPage>
            <PageHeader title={t('routes.title')} description={t('routes.subtitle')} actions={addButton} />
            <ContentCard>
                <FilterRow sx={{ mb: 2 }}>
                    <TextField
                        id="routes-office"
                        select
                        size="small"
                        label={t('routes.office')}
                        value={offices.length ? office_id || '' : ''}
                        onChange={(event) => setOffice_id(event.target.value)}
                        disabled={offices.length === 0}
                        helperText={offices.length === 0 ? t('routes.noOfficesAvailable') : undefined}
                        fullWidth
                    >
                        {offices.map((office: Office) => (
                            <MenuItem key={office.id} value={office.id}>
                                {office.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FilterRow>
                <DataTable<RouteRow>
                    rows={rows}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('routes.title')}
                    searchable
                    searchPlaceholder={t('routes.searchPlaceholder')}
                    exportFileName={t('routes.exportFileName')}
                    initialSort={{ columnId: 'route', direction: 'asc' }}
                    columns={[
                        { id: 'route', header: t('routes.route'), value: (r) => r.routeName, sortable: true },
                        {
                            id: 'responsible',
                            header: t('common.salesRep'),
                            value: (r) => r.responsible,
                            render: (r) => (r.assigned ? r.responsible : <StatusChip label={r.responsible} tone="warning" />),
                            sortable: true,
                        },
                        { id: 'office', header: t('routes.office'), value: (r) => r.officeName, sortable: true, hideBelow: 'sm' },
                        { id: 'time', header: t('routes.schedule'), value: (r) => r.time, hideBelow: 'md' },
                        { id: 'startDate', header: t('routes.ingressDate'), value: (r) => r.startDate, sortable: true, hideBelow: 'md' },
                    ]}
                    rowActions={(row) => (
                        <RowActions>
                            <RowAction
                                label={`${t('common.edit')} ${row.routeName}`}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                allowed={canUpdate}
                                deniedTooltip={t('common.noPermissionEdit')}
                                to={`/admin/routes/${row.id}/edit`}
                            />
                            <RowAction
                                label={`${t('common.delete')} ${row.routeName}`}
                                icon={<DeleteOutlineIcon fontSize="small" />}
                                color="error"
                                allowed={canDelete}
                                deniedTooltip={t('common.noPermissionDelete')}
                                onClick={() => handleDeleteRoute(row)}
                            />
                        </RowActions>
                    )}
                    emptyState={
                        <EmptyState
                            icon={<AltRouteOutlinedIcon />}
                            title={office_id ? t('routes.emptyTitle') : t('routes.chooseOffice')}
                            description={office_id ? t('routes.emptyDescription') : undefined}
                            action={office_id && canCreate ? addButton : undefined}
                        />
                    }
                />
            </ContentCard>
            {confirmDialog}
        </AdminPage>
    );
};

export default RouteList;
